import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import invoicingApi from '../api/invoicingApi';

/**
 * Owner-only invoicing screen: aggregates the invoices Stripe already owns for a date range,
 * reconciles one-off payments into silent Stripe invoices, and exports CSV / a ZIP of PDFs.
 *
 * Replaces the standalone panel that used to be served from the API host at /admin-panel/. Living
 * inside the web app means it reuses the existing session (no second login) and the app's routing.
 * Access is gated twice: this component redirects non-admins, and the Settings entry that links
 * here only renders for admins. The backend independently enforces ROLE_ADMIN.
 */

const ADMIN = '/admin/invoicing';

/** One silent-invoice batch per request; the server reports where to continue. */
const SYNC_BATCH_SIZE = 20;
/** Backstop so a server that never reports `done` cannot spin forever. */
const SYNC_MAX_BATCHES = 60;

type Invoice = {
    id: string;
    number: string | null;
    customerName: string | null;
    customerEmail: string | null;
    issueDate: string | null;
    total: number | null;
    currency: string | null;
    status: string | null;
    source: string | null;
    hostedUrl: string | null;
};

type Stats = {
    revenue: number | null;
    count: number | null;
    paidCount: number | null;
    currency: string | null;
};

/* ---------------- formatting ---------------- */

function fmtMoney(amount: number | null | undefined, currency?: string | null): string {
    if (amount === null || amount === undefined) return '—';
    const num = typeof amount === 'number' ? amount : parseFloat(String(amount));
    if (isNaN(num)) return '—';
    try {
        return new Intl.NumberFormat('sk-SK', {
            style: 'currency',
            currency: (currency || 'EUR').toUpperCase(),
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    } catch {
        return `${num.toFixed(2)} ${(currency || 'EUR').toUpperCase()}`;
    }
}

/** "YYYY-MM-DD" -> "14. 7. 2026" without timezone drift. */
function fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    const parts = String(iso).split('-');
    if (parts.length < 3) return String(iso);
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2].substring(0, 2), 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return String(iso);
    return `${d}. ${m}. ${y}`;
}

const STATUS_LABELS: Record<string, { label: string; tone: 'ok' | 'due' | 'void' }> = {
    paid: { label: 'Zaplatené', tone: 'ok' },
    open: { label: 'Nezaplatené', tone: 'due' },
    uncollectible: { label: 'Nevymožiteľné', tone: 'due' },
    void: { label: 'Stornované', tone: 'void' },
    draft: { label: 'Koncept', tone: 'void' },
};

function sourceLabel(source: string | null): string {
    if (source === 'SUBSCRIPTION') return 'predplatné';
    if (source === 'ONE_OFF') return 'jednorazové';
    return source || '';
}

function pad(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

function toIso(dt: Date): string {
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/** Default range: the current calendar month. */
function defaultRange(): { from: string; to: string } {
    const now = new Date();
    return {
        from: toIso(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: toIso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
}

function safeName(s: string): string {
    return String(s || 'export').replace(/[^0-9a-zA-Z._-]+/g, '-');
}

/** Save a fetched blob to disk via a transient object URL. */
function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/* ---------------- component ---------------- */

const Invoicing: React.FC = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useSelector((state: any) => state.auth);

    const initial = defaultRange();
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [status, setStatus] = useState('');
    const [q, setQ] = useState('');

    const [stats, setStats] = useState<Stats | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState<'' | 'sync' | 'csv' | 'zip'>('');
    const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);

    // Admin gate. The Settings entry is already admin-only, but guard the route itself too:
    // someone could navigate here directly.
    useEffect(() => {
        if (isAuthenticated && !user?.isAdmin) {
            navigate('/');
        }
    }, [isAuthenticated, user, navigate]);

    const rangeParams = useCallback(
        (includeFilters: boolean) => {
            const params: Record<string, string> = {};
            if (from) params.from = from;
            if (to) params.to = to;
            if (includeFilters) {
                if (status) params.status = status;
                if (q.trim()) params.q = q.trim();
            }
            return params;
        },
        [from, to, status, q]
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [statsRes, listRes] = await Promise.all([
                invoicingApi.get(`${ADMIN}/stats`, { params: rangeParams(false) }),
                invoicingApi.get(`${ADMIN}/invoices`, { params: rangeParams(true) }),
            ]);
            setStats(statsRes.data || null);
            const list = Array.isArray(listRes.data) ? listRes.data : listRes.data?.content || [];
            setInvoices(list);
        } catch (err: any) {
            setStats(null);
            setInvoices([]);
            // `message` first: `error` is the machine code ('forbidden'), which used to be absent
            // and now would render to the owner as bare prose.
            const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
            setError(
                serverMsg ||
                    (err?.response?.status === 403
                        ? 'Prístup zamietnutý. Účet nemá oprávnenie správcu (ROLE_ADMIN).'
                        : 'Nepodarilo sa načítať faktúry zo Stripe. Skúste to znova.')
            );
        } finally {
            setLoading(false);
        }
    }, [rangeParams]);

    // Initial load only; afterwards the user applies filters explicitly.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Reconcile one-off payments into silent Stripe invoices. The server processes a bounded slice
     * per call and returns `nextOffset`, so no single request can outlive the proxy timeout. Always
     * continue from the returned offset — restarting at 0 would re-examine charges just invoiced,
     * and Stripe's search index lags, which would create duplicates.
     */
    const runSync = useCallback(
        async (quiet: boolean) => {
            const totals = { created: 0, skipped: 0, failed: 0 };
            let offset = 0;
            for (let n = 0; n < SYNC_MAX_BATCHES; n++) {
                const res = await invoicingApi.post(`${ADMIN}/sync`, null, {
                    params: { ...rangeParams(false), offset, limit: SYNC_BATCH_SIZE },
                });
                const r = res.data || {};
                totals.created += r.created || 0;
                totals.skipped += r.skipped || 0;
                totals.failed += r.failed || 0;

                const next = typeof r.nextOffset === 'number' ? r.nextOffset : offset;
                if (r.done === true || next <= offset) break;
                offset = next;
                if (!quiet) {
                    setNotice(`Synchronizujem… ${offset}/${r.total || '?'}`);
                }
            }
            return totals;
        },
        [rangeParams]
    );

    const onSync = async () => {
        setBusy('sync');
        setError(null);
        setNotice('Synchronizujem…');
        try {
            const t = await runSync(false);
            setNotice(
                t.created > 0
                    ? `Hotovo — vytvorených ${t.created} faktúr pre jednorazové platby.`
                    : 'Hotovo — všetky jednorazové platby už boli spárované.'
            );
            await load();
        } catch (err: any) {
            setNotice(null);
            setError(err?.response?.data?.message || err?.response?.data?.error || 'Synchronizácia zlyhala.');
        } finally {
            setBusy('');
        }
    };

    const onExport = async (format: 'csv' | 'zip') => {
        setBusy(format);
        setError(null);
        setNotice(format === 'zip' ? 'Pripravujem ZIP pre účtovníka…' : 'Pripravujem CSV report…');
        try {
            // Export is READ-ONLY on purpose. It used to run a sync first so freshly-paid one-offs
            // appeared in the report, which meant pressing "Export CSV" wrote invoices to Stripe —
            // and those invoices fired invoice.paid webhooks that truncated live memberships.
            // Downloading a report must never change anything. Press "Synchronizovať jednorazové"
            // first if the month has new one-off payments.
            const params: Record<string, string> = { ...rangeParams(false), format };
            if (status) params.status = status;
            const res = await invoicingApi.get(`${ADMIN}/export`, { params, responseType: 'blob' });
            saveBlob(res.data, `faktury_${safeName(from)}_${safeName(to)}.${format}`);
            setNotice(null);
        } catch (err: any) {
            setNotice(null);
            setError('Sťahovanie zlyhalo.');
        } finally {
            setBusy('');
        }
    };

    const onDownloadPdf = async (inv: Invoice) => {
        setPdfBusyId(inv.id);
        setError(null);
        try {
            const res = await invoicingApi.get(`${ADMIN}/invoices/${encodeURIComponent(inv.id)}/pdf`, {
                responseType: 'blob',
            });
            saveBlob(res.data, `faktura_${safeName(inv.number || inv.id)}.pdf`);
        } catch {
            setError('PDF sa nepodarilo stiahnuť.');
        } finally {
            setPdfBusyId(null);
        }
    };

    const currency = stats?.currency || 'EUR';
    // Average per PAID invoice: revenue counts paid invoices only, so dividing by the total count
    // would be skewed by unpaid/void invoices that contribute no revenue.
    const denom = stats?.paidCount != null ? stats.paidCount : stats?.count;
    const avg = denom && stats?.revenue != null ? Number(stats.revenue) / denom : null;

    return (
        <Container>
            <Header>
                <BackButton onClick={() => navigate(-1)}>&larr;</BackButton>
                <Heading>Faktúry</Heading>
            </Header>

            <Filters>
                <Field>
                    <Label htmlFor="inv-from">Od</Label>
                    <Input id="inv-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </Field>
                <Field>
                    <Label htmlFor="inv-to">Do</Label>
                    <Input id="inv-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </Field>
                <Field>
                    <Label htmlFor="inv-status">Stav</Label>
                    <Select id="inv-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">Všetky</option>
                        <option value="paid">Zaplatené</option>
                        <option value="open">Nezaplatené</option>
                        <option value="void">Stornované</option>
                        <option value="uncollectible">Nevymožiteľné</option>
                    </Select>
                </Field>
                <Field style={{ flex: 2 }}>
                    <Label htmlFor="inv-q">Hľadať</Label>
                    <Input
                        id="inv-q"
                        type="search"
                        placeholder="Meno, e-mail alebo číslo faktúry"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') load();
                        }}
                    />
                </Field>
                <PrimaryButton onClick={load} disabled={loading || !!busy}>
                    {loading ? 'Načítavam…' : 'Zobraziť'}
                </PrimaryButton>
            </Filters>

            <Kpis>
                <Kpi>
                    <KpiLabel>Tržby (zaplatené)</KpiLabel>
                    <KpiValue>{loading ? '…' : fmtMoney(stats?.revenue ?? null, currency)}</KpiValue>
                </Kpi>
                <Kpi>
                    <KpiLabel>Počet faktúr</KpiLabel>
                    <KpiValue>{loading ? '…' : stats?.count ?? '—'}</KpiValue>
                </Kpi>
                <Kpi>
                    <KpiLabel>Priemer na zaplatenú</KpiLabel>
                    <KpiValue>{loading ? '…' : avg != null ? fmtMoney(avg, currency) : '—'}</KpiValue>
                </Kpi>
                <Kpi>
                    <KpiLabel>Obdobie</KpiLabel>
                    <KpiValue small>
                        {fmtDate(from)} – {fmtDate(to)}
                    </KpiValue>
                </Kpi>
            </Kpis>

            <Actions>
                <SecondaryButton onClick={onSync} disabled={!!busy || loading}>
                    {busy === 'sync' ? 'Synchronizujem…' : 'Synchronizovať jednorazové'}
                </SecondaryButton>
                <SecondaryButton onClick={() => onExport('csv')} disabled={!!busy || loading}>
                    {busy === 'csv' ? 'Pripravujem…' : 'Export CSV'}
                </SecondaryButton>
                {/*
                  ZIP export is hidden for now: the deployed backend fails to fetch the invoice PDFs
                  from Stripe (its invoice_pdf URL answers 302 and the server's HTTP client does not
                  follow redirects), so the archive comes back with only index.csv and the browser
                  reports a failed download. `onExport('zip')` already works — restore this button
                  once the gateway fix is deployed:
                  <SecondaryButton onClick={() => onExport('zip')} disabled={!!busy || loading}>
                      {busy === 'zip' ? 'Pripravujem…' : 'ZIP pre účtovníka'}
                  </SecondaryButton>
                */}
            </Actions>

            {/* Export no longer syncs, so say where new one-off payments come from. */}
            <Hint>
                Export sťahuje iba to, čo už je vo Faktúrach. Ak v období pribudli jednorazové platby,
                spustite najprv „Synchronizovať jednorazové“.
            </Hint>

            {error && <Banner role="alert">{error}</Banner>}
            {notice && !error && <Banner as="div" $ok>{notice}</Banner>}

            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <Th>Číslo</Th>
                            <Th>Zákazník</Th>
                            <Th>Dátum</Th>
                            <Th className="right">Suma</Th>
                            <Th>Stav</Th>
                            <Th>Zdroj</Th>
                            <Th className="right">PDF</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <StateCell colSpan={7}>Načítavam faktúry…</StateCell>
                            </tr>
                        )}
                        {!loading && invoices.length === 0 && (
                            <tr>
                                <StateCell colSpan={7}>Za zvolené obdobie nie sú žiadne faktúry.</StateCell>
                            </tr>
                        )}
                        {!loading &&
                            invoices.map((inv) => {
                                const st = STATUS_LABELS[(inv.status || '').toLowerCase()];
                                return (
                                    <tr key={inv.id}>
                                        <Td>{inv.number || inv.id}</Td>
                                        <Td>
                                            <strong>{inv.customerName || '—'}</strong>
                                            <Muted>{inv.customerEmail || ''}</Muted>
                                        </Td>
                                        <Td>{fmtDate(inv.issueDate)}</Td>
                                        <Td className="right">{fmtMoney(inv.total, inv.currency || currency)}</Td>
                                        <Td>
                                            <Tag $tone={st?.tone || 'void'}>{st?.label || inv.status || '—'}</Tag>
                                        </Td>
                                        <Td>
                                            <Muted as="span">Stripe {sourceLabel(inv.source)}</Muted>
                                        </Td>
                                        <Td className="right">
                                            <LinkButton
                                                onClick={() => onDownloadPdf(inv)}
                                                disabled={pdfBusyId === inv.id}
                                            >
                                                {pdfBusyId === inv.id ? '…' : 'Stiahnuť'}
                                            </LinkButton>
                                        </Td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </Table>
            </TableWrap>

            <Foot>
                Zdroj údajov: Stripe. Faktúry vytvára a čísluje Stripe — táto stránka ich iba zobrazuje a
                exportuje.
            </Foot>
        </Container>
    );
};

/* ---------------- styles ---------------- */

const Container = styled.div`
    padding: 20px;
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 20px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    margin-right: 10px;
`;

const Heading = styled.h2`
    margin: 0;
    font-size: 24px;
`;

const Filters = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 20px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 140px;
`;

const Label = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-bottom: 4px;
`;

const Input = styled.input`
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
`;

const Select = styled.select`
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    background: #fff;
`;

const PrimaryButton = styled.button`
    padding: 11px 20px;
    border: none;
    border-radius: 5px;
    background-color: #f56607;
    color: #fff;
    font-weight: bold;
    font-size: 14px;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const SecondaryButton = styled.button`
    padding: 11px 16px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background: #fff;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);

    &:hover:not(:disabled) {
        background: #f0f0f0;
    }
    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
`;

const Kpi = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 14px 16px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
`;

const KpiLabel = styled.div`
    font-size: 12px;
    color: #777;
    margin-bottom: 6px;
`;

const KpiValue = styled.div<{ small?: boolean }>`
    font-size: ${(p) => (p.small ? '14px' : '20px')};
    font-weight: bold;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;
`;

const Hint = styled.p`
    margin: 0 0 18px;
    font-size: 13px;
    line-height: 1.5;
    opacity: 0.72;
`;

const Banner = styled.p<{ $ok?: boolean }>`
    margin: 0 0 16px;
    padding: 12px 14px;
    border-radius: 6px;
    font-size: 14px;
    background: ${(p) => (p.$ok ? '#eef7ee' : '#fdecec')};
    color: ${(p) => (p.$ok ? '#256029' : '#a11')};
`;

const TableWrap = styled.div`
    overflow-x: auto;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    .right {
        text-align: right;
    }
`;

const Th = styled.th`
    text-align: left;
    padding: 12px;
    border-bottom: 1px solid #eee;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #777;
    white-space: nowrap;
`;

const Td = styled.td`
    padding: 12px;
    border-bottom: 1px solid #f2f2f2;
    vertical-align: top;
`;

const StateCell = styled.td`
    padding: 28px 12px;
    text-align: center;
    color: #777;
`;

const Muted = styled.div`
    color: #888;
    font-size: 12px;
`;

const Tag = styled.span<{ $tone: 'ok' | 'due' | 'void' }>`
    display: inline-block;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    background: ${(p) => (p.$tone === 'ok' ? '#e6f4ea' : p.$tone === 'due' ? '#fdecec' : '#eee')};
    color: ${(p) => (p.$tone === 'ok' ? '#1e7e34' : p.$tone === 'due' ? '#a11' : '#555')};
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    color: #f56607;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    padding: 0;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const Foot = styled.p`
    margin-top: 16px;
    font-size: 12px;
    color: #888;
`;

export default Invoicing;
